import { time } from '@/time';
import type { Recipe } from '@/types';

const finishPrep = [
  "Preheat oven to {!300F} with racks in upper & lower-middle positions",
  "Remove ribs from bags & pat dry",
];
const finishRacks = [
  "Line rimmed baking sheets with foil & set wire rack in each",
  "Divide ribs meat-side up between racks",
];

const recipe: Recipe = {
  title: 'Pork Ribs',
  source: 'https://www.seriouseats.com/sous-vide-pork-ribs-recipe-food-lab',
  totalTime: time(0, 30),
  servings: 4,
  tags: { type: 'main' },
  options: [
    {
      key: 'sous-vide',
      default: '24h',
      choices: [
        {
          key: '36h',
          label: '{time} at {!145F}',
          subtitle: 'extra-meaty',
          steps: { cook: { addTime: time(36) } },
        },
        {
          key: '24h',
          label: '{time} at {!155F}',
          subtitle: 'in between',
          steps: { cook: { addTime: time(24) } },
        },
        {
          key: '12h',
          label: '{time} at {!165F}',
          subtitle: 'traditional',
          steps: { cook: { addTime: time(12) } },
        },
      ],
    },
    {
      key: 'finish',
      default: 'sauced',
      choices: [
        { key: 'sauced', label: 'BBQ sauce', steps: { finish: { addTime: time(0, 50) } } },
        { key: 'dry', label: 'Dry rub', steps: { finish: { addTime: time(0, 40) } } },
      ],
    },
  ],
  ingredients: [
    { name: 'pork ribs', quantity: '2', unit: 'racks', customUnit: true,
      note: 'St. Louis–cut, whole',
    },
    { name: 'liquid hickory smoke', quantity: '3/4', unit: 'tsp' },
    { name: 'BBQ pork rub', quantity: '1', unit: 'recipe', customUnit: true,
      recipe: 'bbq-pork-rub',
    },
    { name: 'BBQ sauce', quantity: '1', unit: 'recipe', customUnit: true,
      recipe: 'bbq-sauce',
    },
  ],
  directions: [
    {
      title: 'Prep ribs',
      bullets: [
        "Remove papery membrane on back of {pork ribs} (grip with paper towel & pull off in one piece)",
        "Divide each rack into 3–4 portions, cutting between ribs (3–4 ribs per portion)",
        { text: "Rub generously on all sides with {BBQ pork rub}", when: ['finish', 'sauced'] },
        { text: "Rub generously on all sides with {BBQ pork rub} (reserve {BBQ pork rub:3 tbsp} for dry finish)", when: ['finish', 'dry'] },
      ],
    },
    {
      title: 'Vacuum seal',
      bullets: [
        "Place ribs in sous vide bags",
        "Add 4 drops (~{=fixed 1/8 tsp}) {liquid hickory smoke} per 3-4 rib portion to each bag",
        "Seal bags",
      ],
    },
    {
      title: 'Sous vide',
      bullets: [
        "Add ribs to sous vide bath & cover with lid or foil to prevent evaporation",
        "Cook {opt:sous-vide:cook}",
      ],
    },
    {
      title: 'Finish',
      when: ['finish', 'dry'],
      bullets: [
        ...finishPrep,
        "Rub with reserved {BBQ pork rub:3 tbsp}",
        ...finishRacks,
        "Cook ~40 min until crusty bark forms",
      ],
    },
    {
      title: 'Finish',
      when: ['finish', 'sauced'],
      bullets: [
        ...finishPrep,
        ...finishRacks,
        "Bake 20 min until sizzling & heated through",
        "Brush with {BBQ sauce}",
        "Bake 10 min",
        "Brush again",
        "Bake 10 min more until sauce is dry & sticky",
        "Paint with final layer of sauce",
        "Serve with extra sauce on side",
      ],
    },
  ],
};

export default recipe;
